import { useState, useEffect, useCallback } from 'react';
import { messengerService } from '../services/messengerService';
import type { AgentContact } from '../types/messenger.types';

interface UseAgentsListResult {
  agents: AgentContact[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useAgentsList(): UseAgentsListResult {
  const [agents, setAgents] = useState<AgentContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch agents et derniers messages en parallèle
      const [rawAgents, lastMessages] = await Promise.all([
        messengerService.fetchAgents(),
        messengerService.fetchLastMessages(),
      ]);

      // Enrichir les agents avec leur dernier message
      const enriched: AgentContact[] = rawAgents.map(agent => ({
        ...agent,
        lastMessage: lastMessages[agent.id] ?? undefined,
      }));

      // Trier : agents avec messages récents en premier, puis alphabétique
      enriched.sort((a, b) => {
        const tA = a.lastMessage?.timestamp ?? '';
        const tB = b.lastMessage?.timestamp ?? '';
        if (tA && tB) return tB.localeCompare(tA);
        if (tA) return -1;
        if (tB) return 1;
        return a.name.localeCompare(b.name);
      });

      setAgents(enriched);
    } catch (e: any) {
      setError(e.message || 'Erreur lors du chargement des agents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return { agents, loading, error, refresh: load };
}
